import { Button } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useIsFetching } from 'react-query';
import { useNavigate } from 'react-router';

import { AppModal } from '@/common/components';
import { useAppModalControls } from '@/common/hooks';
import { AppRoutes } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';

import { DomainsQueryKeys, useGetAllDomainsQuery } from '@/modules/Domains';

import { DomainsMultiConfigurationModalStepsView } from './DomainsMultiConfigurationModalStepsView';

interface Props {}

export const DomainsMultiConfigurationModalContainer: FC<Props> = memo(() => {
  const navigate = useNavigate();
  const [modalControl] = useAppModalControls();
  const [currPage, setPage] = useState(1);
  const [modalWidth, setModalWidth] = useState(1600);
  const [checkedDomains, setCheckedDomains] = useState<string[]>([]);

  const { data, isLoading, error, isError, isFetching } = useGetAllDomainsQuery({ page: currPage })({
    enabled: true,
    // staleTime: 1000,
    onSuccess: ({ meta }) => {
      setPage(meta.currentPage);
    },
  });

  const handleOpenModal = () => {
    navigate(AppRoutes.Domains);

    modalControl.open();
  };

  const handleCloseModal = useCallback(() => {
    modalControl.close();
  }, []);

  const handleCheckDomain = useCallback((id: string) => {
    setCheckedDomains((prev) => {
      if (prev.includes(id)) return prev.filter((item) => item !== id);
      return [...prev, id];
    });
  }, []);

  const handleClearCheckedDomains = useCallback(() => {
    setCheckedDomains([]);
  }, []);

  const handleClear = () => {
    setCheckedDomains([]);
    setPage(1);
    setModalWidth(1600);
  };

  const handleChangeModalWidth = useCallback((step: number) => {
    setModalWidth(step === 1 ? 1600 : 620);
  }, []);

  const configureMultiDomainsLoading = !!useIsFetching([DomainsQueryKeys.MultiConfiguration]);

  return (
    <>
      <Button onClick={handleOpenModal}>Конфігурація багатьох доменів</Button>

      <AppModal
        title="Конфігурація багатьох доменів"
        width={modalWidth}
        footer={false}
        centered
        maskClosable={false}
        controls={modalControl}
        afterClose={handleClear}
      >
        <FallbackedLayout data={data} error={error} isError={isError} isLoading={isLoading}>
          {({ meta: { currentPage, totalItems, itemsPerPage }, items }) => (
            <>
              <DomainsMultiConfigurationModalStepsView
                domains={items.map(({ id, name, isConfigured }) => ({ id, name, isConfigured }))}
                pagination={{
                  current: currentPage,
                  total: totalItems,
                  pageSize: itemsPerPage,
                  disabled: isFetching,
                  onChange: setPage,
                }}
                configureMultiDomainsLoading={configureMultiDomainsLoading}
                checkedDomains={checkedDomains}
                handleCloseModal={handleCloseModal}
                handleChangeModalWidth={handleChangeModalWidth}
                handleCheckDomain={handleCheckDomain}
                handleClearCheckedDomains={handleClearCheckedDomains}
              />
            </>
          )}
        </FallbackedLayout>
      </AppModal>
    </>
  );
});
